import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useRef, useState } from "react";

import { ChatMessagePayload, useChatMessageInput } from "./hooks/useChatMessageInput";

import Message from "@/components/Playground/Messages/Message";
import { useChatWidgetContext } from "@/components/Playground/Drawer/Chat/context";
import { MockToolControls } from "@/components/Playground/Drawer/Chat/MockToolControls";
import { cn } from "@/lib/utils";
import { wait } from "@/utils/utils";

interface ChatMessageInputProps {
  onSend: (payload: ChatMessagePayload) => void;
  isLoading?: boolean;
  isToolResponseMode?: boolean;
}

export const ChatInput = observer(
  ({ onSend, isLoading, isToolResponseMode }: ChatMessageInputProps) => {
    const { messageStore, resetInput, getPayload, isEmpty } = useChatMessageInput();
    const { isContainerSizeSmall } = useChatWidgetContext();

    const contentContainerRef = useRef<HTMLDivElement>(null);
    const prevBlocksLengthRef = useRef(messageStore.contentBlocks.length);

    const [isContentScrollable, setIsContentScrollable] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [forceReadonly, setForceReadonly] = useState(false);

    const isExpanded = isHovered || isFocused;

    const handleSend = useCallback(() => {
      if (isEmpty || isLoading) {
        return;
      }

      const payload = getPayload();
      onSend(payload);
      resetInput();
      setForceReadonly(true);
    }, [isEmpty, isLoading, getPayload, onSend, resetInput]);

    /** ------------------------------ EVENT HANDLERS ------------------------------ */
    const handleMouseEnter = () => {
      setIsHovered(true);
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
    };

    const handleFocus = () => {
      setIsFocused(true);
    };

    /**
     * relatedTarget is null when clicking on non-focusable elements (empty space)
     * Only keep focused if relatedTarget exists AND is inside the container
     */
    const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
      const isStillInsideContainer =
        e.relatedTarget && contentContainerRef.current?.contains(e.relatedTarget as Node);
      if (!isStillInsideContainer) {
        setIsFocused(false);
      }
    };

    /** ------------------------------ EFFECTS ------------------------------ */
    /**
     * Restore editability after TipTap syncs cleared content
     */
    useEffect(() => {
      if (forceReadonly) {
        setTimeout(() => {
          setForceReadonly(false);
        }, 300);
      }
    }, [forceReadonly]);

    /**
     * Return focus to the editor after sending a message
     */
    useEffect(() => {
      if (!forceReadonly) {
        const editor = contentContainerRef.current?.querySelector<HTMLElement>(".ProseMirror");
        setTimeout(() => {
          editor?.focus();
        }, 100);
      }
    }, [forceReadonly]);

    /**
     * Check if the content container is scrollable
     */
    useEffect(() => {
      const container = contentContainerRef.current;
      if (!container) {
        return;
      }

      const checkScrollable = () => {
        const scrollable = container.scrollHeight > container.clientHeight;
        setIsContentScrollable(scrollable);
      };

      checkScrollable();

      const resizeObserver = new ResizeObserver(checkScrollable);
      resizeObserver.observe(container);

      const mutationObserver = new MutationObserver(checkScrollable);
      mutationObserver.observe(container, { childList: true, subtree: true, characterData: true });

      return () => {
        resizeObserver.disconnect();
        mutationObserver.disconnect();
      };
    }, []);

    /**
     * Scroll to the bottom of the content container when a new block is added
     */
    useEffect(() => {
      (async () => {
        const currentLength = messageStore.contentBlocks.length;
        if (currentLength > prevBlocksLengthRef.current) {
          const lastBlock = messageStore.contentBlocks[currentLength - 1];

          if (lastBlock.type === "media") {
            await wait(300);
          }

          contentContainerRef.current?.scrollTo({
            top: contentContainerRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
        prevBlocksLengthRef.current = currentLength;
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messageStore.contentBlocks.length]);

    /**
     * Handle click outside of the content container
     */
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (
          contentContainerRef.current &&
          !contentContainerRef.current.contains(e.target as Node)
        ) {
          setIsFocused(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    /**
     * Handle Cmd+Enter to send message when hovering or focused on ChatInput
     */
    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (
          (event.metaKey || event.ctrlKey) &&
          event.key === "Enter" &&
          (isHovered || isFocused) &&
          !isEmpty &&
          !isLoading
        ) {
          event.preventDefault();
          handleSend();
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isHovered, isFocused, isEmpty, isLoading, handleSend]);

    return (
      <div
        className={cn(
          "relative rounded-xl border border-gray-200 bg-white shadow-sm",
          "transition-all duration-300 ease-out",
          isExpanded ? "max-h-[52vh] overflow-y-auto" : "max-h-[118px] overflow-hidden",
        )}
        ref={contentContainerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <Message
          mode="chat"
          message={messageStore}
          onSend={handleSend}
          isSending={isLoading}
          isToolResponseMode={isToolResponseMode}
          secondaryAction={<MockToolControls isSizeXS={isContainerSizeSmall} />}
          readonly={forceReadonly}
          className="px-3 py-2 hover:bg-white"
          contentBlocksClassName="max-h-[28vh] overflow-y-auto"
          isBlockActionsPositionFixed={isContentScrollable}
          isBlockActionsAlwaysVisible
          isBlockActionsSizeXS={isContainerSizeSmall}
        />
      </div>
    );
  },
);
